import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FlaskConical, Gift } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const ref = params.get("ref");
  useEffect(() => { if (ref) localStorage.setItem("parlaylab_ref", ref.toUpperCase()); }, [ref]);
  const storedRef = ref || localStorage.getItem("parlaylab_ref");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form.email, form.password, form.name);
      navigate(location.state?.from || "/dashboard", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto grid min-h-[calc(100vh-4rem)] max-w-6xl items-center gap-12 px-4 py-12 lg:grid-cols-2">
      <div className="hidden lg:block fade-up">
        <p className="label-caps text-emerald-400">Probability-driven betting intelligence</p>
        <h1 className="font-display mt-4 text-5xl font-black uppercase leading-[0.95] tracking-tight text-white lg:text-6xl">
          Enter the <span className="text-emerald-400">Lab.</span><br />Bet the math.
        </h1>
        <p className="mt-6 max-w-md text-base text-slate-400">Daily AI picks across 18 leagues — NFL to NBA, UFC to Formula 1, cricket to horse racing — each with a transparent win probability and the reasoning behind it.</p>
        <div className="mt-8 grid grid-cols-3 gap-4">
          {[["18", "Leagues covered"], ["Claude", "AI analyst"], ["24/7", "Fresh daily slate"]].map(([v, l]) => (
            <div key={l} className="glass-card p-4"><p className="font-display text-3xl font-bold text-white">{v}</p><p className="text-xs text-slate-500">{l}</p></div>
          ))}
        </div>
      </div>

      <div data-testid="auth-modal" className="glass-card mx-auto w-full max-w-md p-8 fade-up" style={{ animationDelay: "100ms" }}>
        <div className="mb-6 flex items-center gap-2 text-emerald-400"><FlaskConical size={20} /><span className="font-display text-xl font-bold uppercase tracking-tight text-white">The Parlay Lab</span></div>
        <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white">{isLogin ? "Welcome back" : "Create your account"}</h2>
        <p className="mt-1 text-sm text-slate-400">{isLogin ? "Log in to see today's picks." : "Start free. Upgrade anytime."}</p>
        {!isLogin && storedRef && (
          <p data-testid="auth-referral-chip" className="mt-3 inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-300"><Gift size={13} /> Invited with code {storedRef.toUpperCase()}</p>
        )}

        <button data-testid="auth-google-login-btn" onClick={loginWithGoogle} className="btn-ghost mt-6 w-full">
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6C12.3 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4H24v8.1h12.8c-.3 2.2-1.7 5.4-4.9 7.6l7.5 5.8c4.5-4.1 7.1-10.2 7.1-17.5z"/><path fill="#FBBC05" d="M10.4 28.8A14.5 14.5 0 0 1 9.5 24c0-1.7.3-3.3.8-4.8l-7.8-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.8-6z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2 1.4-4.7 2.4-8.4 2.4-6.3 0-11.7-4.1-13.6-9.9l-7.8 6C6.5 42.6 14.6 48 24 48z"/></svg>
          Continue with Google
        </button>
        <div className="my-5 flex items-center gap-3 text-xs text-slate-500"><span className="h-px flex-1 bg-[#26334D]" />or with email<span className="h-px flex-1 bg-[#26334D]" /></div>

        <form onSubmit={submit} className="space-y-4">
          {!isLogin && <input data-testid="auth-name-input" className="input-dark" placeholder="Display name" value={form.name} onChange={set("name")} required />}
          <input data-testid="auth-email-input" className="input-dark" type="email" placeholder="Email" value={form.email} onChange={set("email")} required />
          <input data-testid="auth-password-input" className="input-dark" type="password" placeholder="Password (6+ characters)" value={form.password} onChange={set("password")} minLength={6} required />
          {error && <p data-testid="auth-error" className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</p>}
          <button data-testid="auth-submit-btn" disabled={busy} className="btn-primary w-full">{busy ? "Please wait…" : isLogin ? "Log in" : "Create account"}</button>
        </form>
        <p className="mt-5 text-center text-sm text-slate-400">
          {isLogin ? "New to the Lab?" : "Already have an account?"}{" "}
          <Link data-testid="auth-switch-mode-btn" to={isLogin ? "/register" : "/login"} className="font-semibold text-emerald-400 hover:underline">{isLogin ? "Create account" : "Log in"}</Link>
        </p>
      </div>
    </main>
  );
}
